/**
 * generateEventArtwork — RPC contract for the AI event-poster-artwork
 * generation callable.
 *
 * Cross-process contract between the web event-form seam
 * (`event-form.view.ts`'s "Generate with AI" tile, see its own file header)
 * and `firebase/src/events/generateArtwork.api.ts` (onCall
 * handler). Both ends parse with `.strict()` — an extra key fails the whole
 *
 * Fields justified:
 *   prompt     — the creative brief driving every candidate; the poster's
 *                sole authored input.
 *   styleHint  — an optional visual-direction nudge (mood, palette,
 *                technique) layered onto the prompt; the picker's secondary
 *                field.
 *   count      — how many candidates to generate for the picker, capped at
 *                4 by the schema itself — each candidate is a real paid
 *                Imagen call, so the ceiling is load-bearing, not a UI
 *                nicety. Absent → the handler's own default (4).
 *
 * Output:
 *   candidates — the generated poster images, base64-encoded, returned
 *                directly to the client rather than written to Storage: a
 *                brand-new event has no `eventId` yet at pick time (the
 *                poster Storage rule is keyed by `{eventId}`), so the
 *                upload-once-chosen step belongs to the client, against the
 *                existing `tickets.events.manage` + mfa Storage rule, once
 *                the event doc id exists.
 *
 * Excluded:
 *   eventId / storagePath — this callable's job ends at "generate
 *                candidates"; persisting the winner is a client-side upload,
 *                out of scope here.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

export const GenerateEventArtworkInput = z
	.object({
		orgId: Id,
		prompt: z
			.string()
			.min(1)
			.max(500)
			.describe("the creative brief for the poster artwork"),
		styleHint: z
			.string()
			.min(1)
			.max(200)
			.nullish()
			.describe(
				"a visual-direction nudge — mood, palette, or technique — layered onto the prompt",
			),
		count: z
			.number()
			.int()
			.min(1)
			.max(4)
			.nullish()
			.describe(
				"how many candidates to generate, capped at 4; absent defaults to the handler’s own ceiling",
			),
	})
	.strict();
export type GenerateEventArtworkInput = z.infer<
	typeof GenerateEventArtworkInput
>;

export const GenerateEventArtworkOutput = z
	.object({
		candidates: z
			.array(
				z.object({
					imageBase64: z
						.string()
						.min(1)
						.describe(
							"base64-encoded bytes for one generated poster candidate",
						),
					contentType: z
						.string()
						.min(1)
						.describe("the MIME type of the generated image bytes"),
				}),
			)
			.min(1)
			.describe("the generated poster candidates, in generation order"),
	})
	.strict();
export type GenerateEventArtworkOutput = z.infer<
	typeof GenerateEventArtworkOutput
>;
